import React, { useState, useEffect, useMemo } from "react";
import { FaChevronLeft, FaChevronRight, FaPlay, FaPause } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import partyLedgerImg from "../assets/Screenshots/Party Ledger.png";
import accountsTransactionsImg from "../assets/Screenshots/Accounts Transactions.png";
import itemLedgerImg from "../assets/Screenshots/Item Ledger.png";
import salesInvoiceImg from "../assets/Screenshots/Sales Invoice.png";
import loginImg from "../assets/Screenshots/Login .png";
import purchaseVoucherImg from "../assets/Screenshots/Purchase Voucher.png";
import recieptVoucherImg from "../assets/Screenshots/Reciept Voucher.png";
import incomeStatementImg from "../assets/Screenshots/Income Statement.png";

const defaultTitles = [
  "Login",
  "Sales Invoice",
  "Purchase Voucher",
  "Reciept Voucher",
  "Accounts Transactions",
  "Party Ledger",
  "Item Ledger",
  "Income Statement",
];

function ScreenshotSlider() {
  const { t } = useTranslation();
  const screenshots = useMemo(() => {
    const titlesRaw = t("screenshotNames", { returnObjects: true });
    const titles =
      Array.isArray(titlesRaw) && titlesRaw.length === defaultTitles.length ? titlesRaw : defaultTitles;

    const sources = [
      loginImg,
      salesInvoiceImg,
      purchaseVoucherImg,
      recieptVoucherImg,
      accountsTransactionsImg,
      partyLedgerImg,
      itemLedgerImg,
      incomeStatementImg,
    ];

    return sources.map((src, i) => ({ src, title: titles[i] || defaultTitles[i] }));
  }, [t]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlay) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === screenshots.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlay, screenshots.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? screenshots.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === screenshots.length - 1 ? 0 : currentIndex + 1);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative max-w-5xl mx-auto">
      {/* Main Carousel */}
      <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-gray-800">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {screenshots.map((screenshot, index) => (
            <div key={index} className="w-full flex-shrink-0 relative bg-slate-100 dark:bg-slate-900">
              <img 
                src={screenshot.src} 
                alt={`Business Finance Screenshot ${index + 1}`}
                className="w-full h-56 sm:h-64 md:h-72 lg:h-80 object-contain p-2 md:p-3"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent"></div>
              <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 text-white">
                <h3 className="text-base md:text-lg font-semibold">{screenshot.title}</h3>
                <p className="text-sm opacity-90">{t("screenshotCounter")} {index + 1} {t("of")} {screenshots.length}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-white p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        >
          <FaChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-white p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        >
          <FaChevronRight className="w-5 h-5" />
        </button>

        {/* Auto-play Toggle */}
        <button
          onClick={() => setIsAutoPlay(!isAutoPlay)}
          className="absolute top-4 right-4 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-white p-2 rounded-full shadow-lg transition-all duration-300"
        >
          {isAutoPlay ? <FaPause className="w-4 h-4" /> : <FaPlay className="w-4 h-4" />}
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-6 space-x-2">
        {screenshots.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-blue-600 scale-125' 
                : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
            }`}
          />
        ))}
      </div>

      {/* Thumbnail Strip */}
      <div className="flex justify-center mt-4 space-x-2 overflow-x-auto pb-2">
        {screenshots.map((screenshot, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden transition-all duration-300 ${
              index === currentIndex 
                ? 'ring-2 ring-blue-600 scale-110' 
                : 'opacity-60 hover:opacity-100 hover:scale-105'
            }`}
          >
            <img 
              src={screenshot.src} 
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default ScreenshotSlider;
